import React from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";

import VisibilityIcon from "@material-ui/icons/Visibility";
import AddIcon from "@material-ui/icons/Add";
import RefreshIcon from "@material-ui/icons/Refresh";

const styles = (theme) => ({
  root: {
    marginTop: theme.spacing(2),
  },
  actionBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  table: {
    minWidth: 650,
  },
  statusChip: {
    minWidth: 100,
  },
  viewButton: {
    marginRight: theme.spacing(1),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  tableRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  orderIdCell: {
    fontFamily: 'monospace',
    fontSize: '0.9rem',
  },
});

const hebrewLabels = {
  title: "ניהול הזמנות טיוטה",
  actions: {
    createNew: "צור הזמנה חדשה",
    refresh: "רענן",
    viewDetails: "צפה בפרטים",
  },
  table: {
    id: "מזהה",
    status: "סטטוס",
    createdAt: "נוצר בתאריך",
    totalItems: "מספר פריטים",
    totalTires: "מספר צמיגים",
    createdBy: "נוצר על ידי",
    actions: "פעולות",
  },
  status: {
    initial: "התחלתי",
    preparing: "בהכנה",
    completed: "הושלם",
    failed: "נכשל",
    cancelled: "בוטל",
  },
  emptyState: "אין הזמנות טיוטה זמינות",
  clickToView: "לחץ על שורה כדי לצפות בפרטים",
};

class DraftOrdersManager extends React.Component {
  state = {
    loading: false,
  };

  componentDidMount() {
    // Load draft orders when component mounts
    this.props.actions.getDraftOrders();
  }

  handleViewOrder = (order) => {
    // Navigate to the detail page
    this.props.history.push(`/admin/draft-orders/${order._id}`);
  };

  handleRowClick = (order) => {
    this.handleViewOrder(order);
  };

  handleRefresh = () => {
    this.setState({ loading: true });
    this.props.actions.getDraftOrders().finally(() => {
      this.setState({ loading: false });
    });
  };

  handleCreateNew = () => {
    this.setState({ loading: true });
    this.props.actions.createDraftOrder().finally(() => {
      this.setState({ loading: false });
      // Refresh the list after creation
      this.props.actions.getDraftOrders();
    });
  };

  getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "primary";
      case "preparing":
        return "secondary";
      case "failed":
        return "default";
      case "cancelled":
        return "default";
      default:
        return "default";
    }
  };

  formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("he-IL") + " " + date.toLocaleTimeString("he-IL", { hour: '2-digit', minute: '2-digit' });
  };

  render() {
    const { classes, draftOrders } = this.props;
    const { loading } = this.state;

    return (
      <div className={classes.root}>
        <div className={classes.actionBar}>
          <Typography variant="h5">
            {hebrewLabels.title}
          </Typography>
          <div>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={this.handleCreateNew}
              disabled={loading}
              className={classes.viewButton}
            >
              {hebrewLabels.actions.createNew}
            </Button>
            <IconButton
              onClick={this.handleRefresh}
              disabled={loading}
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          </div>
        </div>

        {loading && (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        )}

        {!loading && (!draftOrders || draftOrders.length === 0) && (
          <div className={classes.emptyState}>
            <Typography variant="h6">
              {hebrewLabels.emptyState}
            </Typography>
          </div>
        )}

        {!loading && draftOrders && draftOrders.length > 0 && (
          <div>
            <TableContainer component={Paper}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell>{hebrewLabels.table.id}</TableCell>
                    <TableCell>{hebrewLabels.table.status}</TableCell>
                    <TableCell>{hebrewLabels.table.createdAt}</TableCell>
                    <TableCell align="center">{hebrewLabels.table.totalItems}</TableCell>
                    <TableCell align="center">{hebrewLabels.table.totalTires}</TableCell>
                    <TableCell>{hebrewLabels.table.createdBy}</TableCell>
                    <TableCell align="center">{hebrewLabels.table.actions}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {draftOrders.map((order) => (
                    <TableRow 
                      key={order._id} 
                      className={classes.tableRow}
                      onClick={() => this.handleRowClick(order)}
                    >
                      <TableCell className={classes.orderIdCell}>
                        {order._id.substring(0, 8)}...
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={hebrewLabels.status[order.status] || order.status}
                          color={this.getStatusColor(order.status)}
                          size="small"
                          className={classes.statusChip}
                        />
                      </TableCell>
                      <TableCell>
                        {this.formatDate(order.createdAt)}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                          {order.totalItems || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                          {order.totalTires || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>{order.createdBy || "system"}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent row click
                            this.handleViewOrder(order);
                          }}
                          color="primary"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Typography 
              variant="caption" 
              color="textSecondary" 
              style={{ 
                display: 'block', 
                textAlign: 'center', 
                marginTop: 16,
                fontStyle: 'italic'
              }}
            >
              {hebrewLabels.clickToView}
            </Typography>
          </div>
        )}
      </div>
    );
  }
}

DraftOrdersManager.propTypes = {
  classes: PropTypes.object.isRequired,
  draftOrders: PropTypes.array,
  actions: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default withRouter(withStyles(styles)(DraftOrdersManager)); 